import { forwardRef, useMemo } from "react";

import cn from "classnames";
import { observer } from "mobx-react";

import { DateInput, DateInputProps } from "./DateInput";
import { EmailInput, EmailInputProps } from "./EmailInput";
import { NameInput, NameInputProps } from "./NameInput";
import { NumberInput, NumberInputProps } from "./NumberInput";
import { PasswordInput, PasswordInputProps } from "./PasswordInput";
import { PhoneInput, PhoneInputProps } from "./PhoneInput";
import { SearchInput, SearchInputProps } from "./SearchInput";
import { TextInput, TextInputProps } from "./TextInput";

import styles from "./Input.module.scss";

type ConditionalProps =
  | TextInputProps
  | PasswordInputProps
  | EmailInputProps
  | NameInputProps
  | NumberInputProps
  | SearchInputProps
  | DateInputProps
  | PhoneInputProps;

export type InputProps = {
  containerClassName?: string;
} & ConditionalProps;

type InputType = InputProps["type"];

export const Input = observer(
  forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
    const { containerClassName, type } = props;

    const containerClasses = cn(styles.container, containerClassName);

    const InputView = useMemo(() => {
      const inputTypes: Record<InputType, React.ReactNode> = {
        text: <TextInput ref={ref} {...(props as TextInputProps)} />,
        number: <NumberInput ref={ref} {...(props as NumberInputProps)} />,
        name: <NameInput ref={ref} {...(props as NameInputProps)} />,
        email: <EmailInput ref={ref} {...(props as EmailInputProps)} />,
        search: <SearchInput ref={ref} {...(props as SearchInputProps)} />,
        password: (
          <PasswordInput ref={ref} {...(props as PasswordInputProps)} />
        ),
        date: <DateInput ref={ref} {...(props as DateInputProps)} />,
        phone: <PhoneInput ref={ref} {...(props as PhoneInputProps)} />,
      };
      return inputTypes[type];
    }, [props, ref, type]);

    return <div className={containerClasses}>{InputView}</div>;
  })
);
